/**
 * Shared 911 / 988 emergency help.
 * full = Safety page box, compact = dashboards / session / pre-join,
 * line = footer text. Brand navy/sky/pink — no teal.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';

import NamedLink from '../NamedLink/NamedLink';

import css from './EmergencyHelp.module.css';

const PhoneActions = ({ compact }) => (
  <div className={classNames(css.boxActions, { [css.compactActions]: compact })}>
    <a className={css.call911} href="tel:911">
      <FormattedMessage id="ContentPage.help.call911" />
    </a>
    <a className={css.call988} href="tel:988">
      <FormattedMessage id="ContentPage.help.call988" />
    </a>
    <a className={css.text988} href="sms:988">
      <FormattedMessage id="ContentPage.help.text988" />
    </a>
  </div>
);

/**
 * One emergency UI: Safety box, compact box, or footer line.
 *
 * @component
 * @param {Object} [props]
 * @param {'full'|'compact'|'line'} [props.variant]
 * @param {'default'|'session'|'consent'|'footer'|'dashboard'|'safety'} [props.placement]
 * @param {boolean} [props.listenerGuidance]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export const EmergencyHelp = ({
  variant,
  placement = 'default',
  listenerGuidance = false,
  className,
} = {}) => {
  const resolvedVariant = variant || (placement === 'footer' ? 'line' : 'compact');
  const stayOnPage = placement === 'session';
  const titleId = `emergency-help-title-${placement}`;

  if (resolvedVariant === 'line') {
    return (
      <div
        className={classNames(css.lineWrap, css[placement], className)}
        data-emergency-line={placement}
        data-emergency-stay="false"
      >
        <p className={css.lineLead}>
          <FormattedMessage id="EmergencyLine.lead" />
        </p>
        <div className={css.linePills} data-emergency-pills="true">
          <a className={css.call911} href="tel:911">
            <FormattedMessage id="ContentPage.help.call911" />
          </a>
          <a className={css.call988} href="tel:988">
            <FormattedMessage id="ContentPage.help.call988" />
          </a>
          <a className={css.text988} href="sms:988">
            <FormattedMessage id="ContentPage.help.text988" />
          </a>
          <NamedLink className={css.safetyPill} name="SafetyPage">
            <FormattedMessage id="EmergencyLine.safetyLabel" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const isFull = resolvedVariant === 'full';
  const TitleTag = isFull ? 'h2' : 'p';
  return (
    <aside
      className={classNames(css.box, { [css.compact]: !isFull }, css[placement], className)}
      data-emergency-box={isFull ? 'true' : 'compact'}
      data-emergency-line={placement}
      data-emergency-stay={stayOnPage ? 'true' : 'false'}
      aria-labelledby={titleId}
    >
      <TitleTag id={titleId} className={css.boxTitle}>
        <FormattedMessage
          id={isFull ? 'ContentPage.help.emergencyTitle' : 'EmergencyLine.lead'}
        />
      </TitleTag>
      {isFull ? (
        <p className={css.boxBody}>
          <FormattedMessage id="FAQPage.aEmergency" />
        </p>
      ) : null}
      {listenerGuidance ? (
        <p className={css.guidance} data-emergency-listener-guidance="true">
          <FormattedMessage id="EmergencyLine.listenerGuidance" />
        </p>
      ) : null}
      <PhoneActions compact={!isFull} />
      {isFull || stayOnPage ? null : (
        <NamedLink className={css.safetyTips} name="SafetyPage">
          <FormattedMessage id="EmergencyLine.safetyTips" />
        </NamedLink>
      )}
    </aside>
  );
};

/**
 * Full Safety-page emergency box.
 *
 * @component
 * @param {Object} [props]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export const EmergencyHelpBox = ({ className } = {}) => (
  <EmergencyHelp variant="full" placement="safety" className={className} />
);

/**
 * Compact box everywhere except footer, which uses tappable crisis pills.
 *
 * @component
 * @param {Object} [props]
 * @param {'default'|'session'|'consent'|'footer'|'dashboard'} [props.placement]
 * @param {boolean} [props.listenerGuidance]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export const EmergencyLine = ({
  placement = 'default',
  listenerGuidance = false,
  className,
} = {}) => (
  <EmergencyHelp
    variant={placement === 'footer' ? 'line' : 'compact'}
    placement={placement}
    listenerGuidance={listenerGuidance}
    className={className}
  />
);

export default EmergencyHelp;
